import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosinstance";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { Snackbar, Alert as MuiAlert } from "@mui/material"; // Import MuiAlert as Alert
import API_URL from "../Config"; // Adjust the path based on your file structure
import "./Forms.css";
import Select from "react-select";

// Yup validation schema
const validationSchema = Yup.object({
  firm_name: Yup.string().required("Firm Name is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  con_no: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Contact number is required"),
  max_subsidiaries: Yup.number().required("Max Subsidiaries is required"),
  owner_name: Yup.string().required("Owner Name is required"),
  address: Yup.string().required("Address is required"), // Added address validation
});

export default function AdminForm({ isSidebarExpanded }) {
  // Initialize hooks
  const navigate = useNavigate(); // Hook for navigation

  // State for controlling Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [roles, setRoles] = useState([]); // Fetch available roles

  const formik = useFormik({
    initialValues: {
      firm_name: "",
      email: "",
      con_no: "",
      max_subsidiaries: "",
      owner_name: "",
      address: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Convert max_subsidiaries to a number if it's not empty
        const payload = {
          ...values,
          max_subsidiaries: values.max_subsidiaries
            ? Number(values.max_subsidiaries)
            : "", // Convert to number
        };

        const response = await axiosInstance.post("/api/create_firms", payload);

        if (response.status === 200) {
          setSnackbarSeverity("success");
          setSnackbarMessage("Firm added successfully!");
          setSnackbarOpen(true);

          setTimeout(() => {
            navigate("/super-Admin");
          }, 2000);
        } else {
          setSnackbarSeverity("error");
          setSnackbarMessage("Error adding firm. Please try again.");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error submitting the form:", error.message);
        setSnackbarSeverity("error");
        setSnackbarMessage("Error submitting the form. Please try again.");
        setSnackbarOpen(true);
      }
    },
  });

  useEffect(() => {
    // Fetch any roles or predefined data here (if needed)
    const fetchRoles = async () => {
      try {
        const response = await axiosInstance.get("/api/get_all_roles");
        setRoles(response.data.data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
  }, []);

  // Handle save button click to trigger validation
  const handleSaveClick = (e) => {
    e.preventDefault();

    // Mark all fields as touched to show validation errors
    formik.setTouched({
      firm_name: true,
      email: true,
      con_no: true,
      max_subsidiaries: true,
      owner_name: true,
      address: true, // Mark address as touched
    });

    // Trigger validation on all fields
    formik.validateForm().then(() => {
      // If validation is successful, submit the form
      if (!Object.keys(formik.errors).length) {
        formik.handleSubmit();
      }
    });
  };

  // Close snackbar after it's shown
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handlecancel = () => {
    window.history.back();
  };

  return (
    <div
      className={`table-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body">
        <h1 className="form-title jost-bold">Add New Firm</h1>
        <div className="form-body">
          <form className="form-box">
            <div className="form-group">
              <label className="form-label">
                Firm Name <span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="text"
                  className="input-field"
                  name="firm_name"
                  placeholder="Enter Firm Name"
                  value={formik.values.firm_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                  autoFocus
                />
                {formik.touched.firm_name && formik.errors.firm_name && (
                  <div className="error-message">{formik.errors.firm_name}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Email<span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="email"
                  className="input-field"
                  name="email"
                  placeholder="Enter Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="error-message">{formik.errors.email}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Contact Number<span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="tel"
                  className="input-field"
                  name="con_no"
                  placeholder="Enter Contact Number"
                  value={formik.values.con_no}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.con_no && formik.errors.con_no && (
                  <div className="error-message">{formik.errors.con_no}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Max Subsidiaries <span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="number"
                  className="input-field"
                  name="max_subsidiaries"
                  placeholder="Enter Max Subsidiaries"
                  value={formik.values.max_subsidiaries}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.max_subsidiaries &&
                  formik.errors.max_subsidiaries && (
                    <div className="error-message">
                      {formik.errors.max_subsidiaries}
                    </div>
                  )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Owner Name <span className="required">*</span>
              </label>
              <div className="form-inputs">
                <input
                  type="text"
                  className="input-field"
                  name="owner_name"
                  placeholder="Enter Owner Name"
                  value={formik.values.owner_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.owner_name && formik.errors.owner_name && (
                  <div className="error-message">
                    {formik.errors.owner_name}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                Address <span className="required">*</span>
              </label>
              <div className="form-inputs">
                <textarea
                  className="input-field custom-textarea::placeholder"
                  name="address"
                  placeholder="Enter Address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  required
                />
                {formik.touched.address && formik.errors.address && (
                  <div className="error-message">{formik.errors.address}</div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(50px)", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
      <div className="form-footer">
        <div className="button-group">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handlecancel}
          >
            CANCEL
          </button>
          <button
            type="submit"
            className="btn btn-save"
            onClick={handleSaveClick}
          >
            SAVE
          </button>
        </div>
      </div>
    </div>
  );
}
